import { IStyleFunctionOrObject, IStyleSet } from '@fluentui/react';
import _ from 'lodash';

/**
 * This method will merge multiple style sets into one style set.
 * When the styled component is overridden with styles from ThemeProvider or styles property passed in by
 * caller, the props.styles object keep all different TStyleSet from each source. And the order is
 * [0]: defaultStyles
 * [1]: ThemeProvider's overrides
 * [2]: call's overrides
 * Each of them could be a complete TStyleSet or just Partial<TStyleSet>. We need to merge them together
 * to reduce into just one composed TStyleSet. The order of the merge will be [0]->[1]->[2]
 * However, if there is no overrides from caller or ThemeProvider for certain key, that TStyleSet[key]
 * won't be an array. So we will just return as is
 *
 * @param styleProps The TStyleProps object used to calculate styles
 * @param styles Either an IStyleSet object or a function take TStyleProps as input to calculate IStyleSet
 * For example. if the styles
 * @returns a TStyleSet object
 */
export const resolveStyles = <TStyleProps, TStyleSet extends IStyleSet<TStyleSet>>(
  styleProps: TStyleProps,
  styles: IStyleFunctionOrObject<TStyleProps, TStyleSet>
) => {
  // First, let's check if the styles is a function or object. If it's function, call
  // it with styleProps to get the object
  const resolvedStyles = typeof styles === 'function' ? styles(styleProps) : styles;
  for (const key in resolvedStyles) {
    // eslint-disable-next-line no-prototype-builtins
    if (resolvedStyles.hasOwnProperty(key)) {
      // If the resolvedStyles[key] is an array, means it has been overridden by outside sources.
      if (Array.isArray(resolvedStyles[key])) {
        let mergedStyle: IStyleSet<TStyleSet> = {};
        // From [0] to [n], deep merge each property of that object [0] is the base styles,
        // [1] is the global override from ThemeProvider, [2] is the override from caller.
        // Higher index means higher order/priority to override.
        for (let index = 0; index < (resolvedStyles[key] as Array<TStyleSet>).length; index++) {
          mergedStyle = _.merge(mergedStyle, resolvedStyles[key][index]);
        }
        // After merged, replace the array with the merged object
        (resolvedStyles[key] as unknown) = mergedStyle;
      }
    }
  }
  return resolvedStyles;
};
